import { narrowTo } from '../api/vocabulary';

/** The theme vocabulary, spelled once client-side — storage = wire = screen (015 R-002). */
export const THEMES = ['light', 'dark', 'system'] as const;
export type ThemePreference = (typeof THEMES)[number];

const asTheme = narrowTo(THEMES, 'theme');

/**
 * The effective theme is a single `data-theme` attribute on `<html>`, and this module is its only
 * writer (015 R-008). `system` is resolved HERE, in code, not duplicated in CSS: a
 * `@media (prefers-color-scheme: dark)` block would need the whole dark palette emitted a second
 * time, and the palette guards would have to understand a media-scoped rule set. One attribute,
 * one dark block in `tokens.scss`, one resolver.
 *
 * 034: a module with closure state rather than a root-provided class — `providedIn: 'root'` and a
 * module are the same lifetime, and `DOCUMENT` was injected only so a test could substitute it,
 * which `resetModules` now does more directly. It is deliberately NOT a hook: nothing here
 * re-renders, and the four settle points below are imperative calls from outside React.
 *
 * `applyTheme` is called imperatively at the four settle points (data-model §6): the preferences read
 * resolving inside `SessionStore.adopt()`, a save succeeding, the session clearing (→ light,
 * FR-010), and — via the subscription below — an OS change while `system` is in force (FR-008).
 * Not an `effect()`: FR-011's ordering ("in effect from the first authenticated paint") is
 * provable for a call sequence, and an effect's flush timing relative to first paint is scheduler
 * trivia a test would have to trust rather than check (R-010).
 */
/**
 * This module's `matchMedia` subscription; non-null exactly while `system` is in force.
   *
 * **It used to say "the one `matchMedia` subscription in the application", and 024 made that
 * false**: `features/home/home-page.ts` opens a second one for `(pointer: fine)`, which decides
 * whether the drag affordance is offered at all (FR-003). Corrected in the same commit that added
 * the second, because a comment is what the next reader checks *instead of* the code — this
 * repository has paid for a stale one three times (019's `const string`, 020's `finally` block,
 * 023's changeset guard), and each time the wrong sentence was what licensed the wrong change.
 */
let query: MediaQueryList | null = null;
let listener: ((event: MediaQueryListEvent) => void) | null = null;

/**
 * Takes the wire's plain string and narrows it here — the client-side seam for this vocabulary,
 * through the same `narrowTo` `asDateFormatFamily` and `asTitleDateVariant` go through. This one
 * used to test membership with `includes`, which refuses the right values but tells the compiler
 * nothing, so `set` below needed a cast to get the narrow type back; the shared form returns it,
 * and the early return does the rest. The sentence it throws is unchanged.
 */
export function applyTheme(rawPreference: string): void {
  const preference = asTheme(rawPreference);

  if (preference === 'system') {
    if (query === null) {
      query = matchMedia('(prefers-color-scheme: dark)');
      listener = (event) => set(event.matches ? 'dark' : 'light');
      query.addEventListener('change', listener);
    }
    set(query.matches ? 'dark' : 'light');
    return;
  }

  unsubscribe();
  set(preference);
}

/** A direct choice replaces `system`: the OS must no longer be able to move the attribute. */
function unsubscribe(): void {
  if (query !== null && listener !== null) {
    query.removeEventListener('change', listener);
  }
  query = null;
  listener = null;
}

function set(value: 'light' | 'dark'): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', value);
  paintChrome(root);
}

/**
 * The INSTALLED window's chrome — the status bar on a phone, the title bar on a desktop —
   * follows the resolved theme (021 FR-039).
   *
   * Three separate reasons the two `<meta name="theme-color" media="...">` tags in `index.html`
   * could not do this on their own, and only the first is obvious:
   *
   * 1. **They track the OS, not the practitioner.** `prefers-color-scheme` is the system setting;
   *    `data-theme` is the choice made in Settings. Someone running a light OS who picks Dark
   *    matches the LIGHT tag and gets a white status bar around a dark application. The comment
   *    those tags carried claimed they made the chrome follow "the practitioner's theme", which
   *    was true of the intent and false of the mechanism.
   * 2. **A media-scoped tag is evaluated at parse.** Setting one value here, unconditionally,
   *    after the attribute is written, is what makes a live theme change move the chrome.
   * 3. **An installed window prefers the manifest's `theme_color`,** which is ONE static value and
   *    cannot carry two palettes. Overwriting the tag at runtime is the only lever the page has.
   *    Read `contracts/manifest.md`: the manifest's colour still governs the splash and the launch
   *    frame, before any of this code exists, and that is a platform limit rather than a defect.
   *
   * The colour is READ FROM `--paper` rather than written here. A literal in this file would be a
   * second definition of the ground, and the tag it replaces proves the cost: its dark value was
   * the LIGHT theme's `--ink` rather than the dark `--paper`. Close enough to look deliberate,
   * wrong, and waved through a palette guard whose allowlist recorded the value without ever
   * asking whether it equalled the token it claimed to restate. (Writing the two hexes into this
   * very comment is what `hue-count.spec.ts` then caught — it reads comments too.)
   *
   * An empty read means no stylesheet has loaded (a unit host with no `tokens.scss`). Leaving
   * `index.html`'s declarations alone is then strictly better than replacing them with nothing.
   */
function paintChrome(root: HTMLElement): void {
  const ground = window.getComputedStyle(root).getPropertyValue('--paper').trim();
  if (!ground) return;

  for (const stale of Array.from(document.head.querySelectorAll('meta[name="theme-color"]'))) {
    stale.remove();
  }
  const meta = document.createElement('meta');
  meta.setAttribute('name', 'theme-color');
  meta.setAttribute('content', ground);
  document.head.appendChild(meta);
}
